/**
 * Gate hook that every `useGhostwriterFor*` hook calls to ensure
 * the ghostwriter lifecycle is initialized and the user is enrolled
 * in any ghostwriter proctor bucket.
 *
 * Returns four flags:
 *   - `isEnabled` → user is in *any* ghostwriter bucket (active,
 *     phase A, phase B, or phase C). Loading semantics:
 *       - `null` while any proctor check is still resolving
 *       - `true` if any bucket is `true`
 *       - `false` only when all buckets are explicitly `false`
 *   - `isPhaseA` → user is in the phase A (synchronous flow) bucket
 *     specifically. Use to branch UI/copy on flow variant.
 *   - `isPhaseB` → user is in the phase B (skip-eligibility) bucket
 *     specifically. Internal-only signal used by `useGhostwriterFor*`
 *     hooks to bypass the per-job eligibility backend call.
 *   - `isPhaseC` → user is in the phase C bucket specifically (phase A's
 *     synchronous Prefill flow with the standard 'Auto apply' CTA copy).
 *
 * Note: this hook intentionally does *not* expose `isActive`; the
 * gate is the union of buckets, and the variant flags are `isPhaseA`,
 * `isPhaseB`, and `isPhaseC`. Internal code that needs the active-bucket
 * selector specifically can call `useIsGhostwriterActive` from
 * `GhostwriterContext`.
 *
 * The `UserDidLogout` broadcast subscription (Layer 1) is managed
 * by `ghostwriterLifecycle.setupLogoutBroadcast`, called once during
 * the first `ensureGhostwriterInitialized`.
 */

import {
    useIsGhostwriterActive,
    useIsGhostwriterPhaseA,
    useIsGhostwriterPhaseB,
    useIsGhostwriterPhaseC,
} from './GhostwriterContext';
import {
    ensureGhostwriterInitialized,
    waitForAccountId,
} from './ghostwriterLifecycle';
import { useTareData } from '../../shared/native-modules/tare/TareDataContext';

import { useEffect } from 'react';
import { Platform } from 'react-native';

export interface GhostwriterGateResult {
    isEnabled: boolean | null;
    isPhaseA: boolean | null;
    isPhaseB: boolean | null;
    isPhaseC: boolean | null;
}

function deriveIsEnabled(
    isActive: boolean | null,
    isPhaseA: boolean | null,
    isPhaseB: boolean | null,
    isPhaseC: boolean | null,
): boolean | null {
    if (isActive === true || isPhaseA === true || isPhaseB === true || isPhaseC === true) return true;
    if (isActive === null || isPhaseA === null || isPhaseB === null || isPhaseC === null) return null;
    return false;
}

export function useGhostwriterGate(): GhostwriterGateResult {
    const tareData = useTareData();
    const accountId = tareData.accountStatus?.accountId ?? null;
    const isLoggedIn = tareData.accountStatus?.isLoggedIn;
    const isActive = useIsGhostwriterActive();
    const isPhaseA = useIsGhostwriterPhaseA();
    const isPhaseB = useIsGhostwriterPhaseB();
    const isPhaseC = useIsGhostwriterPhaseC();

    useEffect(() => {
        if (accountId) {
            void ensureGhostwriterInitialized(accountId);
            return;
        }

        // On Android, accountStatus.accountId may not be populated at
        // mount time even when the user is logged in. Wait for the
        // native DataUpdated broadcast, then re-read account status.
        if (Platform.OS === 'android' && isLoggedIn !== false) {
            let cancelled = false;
            void waitForAccountId().then((resolvedId) => {
                if (!cancelled) {
                    void ensureGhostwriterInitialized(resolvedId);
                }
            });
            return () => {
                cancelled = true;
            };
        }

        void ensureGhostwriterInitialized(null);
    }, [accountId, isLoggedIn]);

    return {
        isEnabled: deriveIsEnabled(isActive, isPhaseA, isPhaseB, isPhaseC),
        isPhaseA,
        isPhaseB,
        isPhaseC,
    };
}
